import { useEffect, useMemo, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';

import BuildLineup from './BuildLineup';
import type { SlotLocation } from './BuildLineup';
import { PlayerListPanel } from './PlayerListPanel';
import Button from '@components/atoms/button';
import { WarningAlert } from '@components/alert';
import { BodyRegular, H3 } from '@components/atoms/typography';
import { useAccessToken } from '@hooks/useAccessToken';
import { useSquadFixtures } from '@hooks/my-team/useSquadFixtures';
import { TRANSFER_RECOMMENDATIONS_QUERY_KEY } from '@hooks/useTransferRecommendations';
import { importTeamFromScreenshot } from '@lib/api/import-team';
import { PlannerApiError, upsertPlanner } from '@api-service/planner';
import type { Player } from '@api-service/players';
import {
  allSquadPlayers,
  buildReviewLineup,
  composition,
  findLocation,
  isReviewLineupValid,
  pickDefaultCaptain,
  pickDefaultViceCaptain,
  REQUIRED,
  setSlot,
  toSlotOrderedPicks,
  totalSquadPrice,
  type BuildPlayer,
  type BuildPosition,
  type ReviewLineup
} from '@lib/my-team/build-review-lineup';
import {
  findClubOverflowInSquad,
  MAX_PLAYERS_PER_CLUB
} from '@lib/my-team/utils/club-overflow';

// Pre-gameweek-one squad budget. FPL gives every manager £100.0m to build their
// initial 15; the backend enforces this too, so this is for the running-bank
// display only.
const BUDGET_CAP = 100;

// A full FPL squad is 15 players (2 GK, 5 DEF, 5 MID, 3 FWD).
const SQUAD_SIZE = 15;

const POSITION_LABEL: Record<BuildPosition, string> = {
  GK: 'goalkeepers',
  DEF: 'defenders',
  MID: 'midfielders',
  FWD: 'forwards'
};

const toBuildPlayer = (player: {
  id: string;
  displayName: string;
  position: BuildPosition;
  team: { shortName: string };
  kitImageUrl?: string;
  price: number;
}): BuildPlayer => ({
  id: player.id,
  displayName: player.displayName,
  position: player.position,
  team: { shortName: player.team.shortName },
  kitImageUrl: player.kitImageUrl,
  price: player.price
});

const fileToBase64 = (file: File): Promise<string> =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => {
      const result = reader.result as string;
      // Strip the `data:image/...;base64,` prefix - the backend wants the
      // raw base64 payload.
      resolve(result.split(',')[1] ?? '');
    };
    reader.onerror = () => reject(new Error('Could not read the image file'));
    reader.readAsDataURL(file);
  });

const remainingFor = (counts: Record<BuildPosition, number>) =>
  (['GK', 'DEF', 'MID', 'FWD'] as const)
    .filter((position) => counts[position] < REQUIRED[position])
    .map(
      (position) =>
        `${REQUIRED[position] - counts[position]} ${POSITION_LABEL[position]}`
    );

type BuildTeamProps = {
  /**
   * Non-paying members can build a team, but predicted points outside the
   * free gameweek stay padlocked in the player picker.
   */
  isLocked?: boolean;
  onTeamSaved?: () => void;
  /**
   * When the build flow replaces an existing team (reset pre-gameweek-one),
   * gives the user a way back without losing their current team. Saving
   * overwrites the old team; cancelling keeps it untouched.
   */
  onCancel?: () => void;
};

export const BuildTeam = ({
  isLocked = false,
  onTeamSaved,
  onCancel
}: BuildTeamProps) => {
  const { accessToken } = useAccessToken();
  const queryClient = useQueryClient();
  const playerListRef = useRef<HTMLDivElement | null>(null);

  const [lineup, setLineup] = useState<ReviewLineup>(() =>
    buildReviewLineup([])
  );
  const [captainId, setCaptainId] = useState<string | null>(null);
  const [viceCaptainId, setViceCaptainId] = useState<string | null>(null);
  const [replaceTarget, setReplaceTarget] = useState<SlotLocation | null>(null);
  const [isImporting, setIsImporting] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [importError, setImportError] = useState<string | null>(null);
  const [submitErrors, setSubmitErrors] = useState<string[]>([]);

  const isValid = useMemo(() => isReviewLineupValid(lineup), [lineup]);
  const counts = useMemo(() => composition(lineup), [lineup]);
  const squadPlayers = useMemo(() => allSquadPlayers(lineup), [lineup]);
  const squadIds = useMemo(
    () => squadPlayers.map((player) => player.id),
    [squadPlayers]
  );
  // The backend rejects a squad with four players from one club, but only with
  // a generic message, so name the club here as soon as the fourth is picked.
  const clubOverflow = useMemo(
    () => findClubOverflowInSquad(squadPlayers),
    [squadPlayers]
  );
  const { fixturesMap } = useSquadFixtures(squadIds);
  const spent = useMemo(() => totalSquadPrice(lineup), [lineup]);
  const remainingBank = BUDGET_CAP - spent;

  // Default the armbands once the squad is complete, and fill either one back
  // in if the player wearing it is removed. Both are defaulted independently:
  // an explicit choice the user has already made stays put, but leaving one
  // empty would block the save with nothing on screen to explain why.
  useEffect(() => {
    if (!isValid || (captainId && viceCaptainId)) return;
    const captain = captainId ?? pickDefaultCaptain(lineup);
    setCaptainId(captain);
    setViceCaptainId(
      viceCaptainId && viceCaptainId !== captain
        ? viceCaptainId
        : pickDefaultViceCaptain(lineup, captain)
    );
  }, [isValid, captainId, viceCaptainId, lineup]);

  const scrollToPicker = () => {
    playerListRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'start'
    });
  };

  const handleScreenshotChange = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    // Reset the input so re-selecting the same file fires onChange again.
    event.target.value = '';
    if (!file) return;

    setImportError(null);
    setIsImporting(true);
    try {
      const base64Image = await fileToBase64(file);
      const { team } = await importTeamFromScreenshot(base64Image);
      const players = team.players.map((match) => toBuildPlayer(match.player));
      setLineup(buildReviewLineup(players));
      setCaptainId(null);
      setViceCaptainId(null);
    } catch {
      setImportError(
        "We couldn't read that screenshot. Try another image or build your team manually."
      );
    } finally {
      setIsImporting(false);
    }
  };

  const handleEmptySlotPress = (location: SlotLocation) => {
    setReplaceTarget(location);
    scrollToPicker();
  };

  const handleSelectPlayer = (player: Player) => {
    const incoming = toBuildPlayer(player);
    const target =
      replaceTarget ?? firstEmptySlotFor(lineup, incoming.position);
    if (!target) return;

    const previous = lineup[target.position][target.index];
    setLineup((current) => setSlot(current, target, incoming));

    // Keep captain/vice pointing at the right player if we replaced one.
    if (previous && previous.id === captainId) setCaptainId(incoming.id);
    if (previous && previous.id === viceCaptainId)
      setViceCaptainId(incoming.id);

    setReplaceTarget(null);
  };

  const handleRemovePlayer = (playerId: string) => {
    const location = findLocation(lineup, playerId);
    if (!location) return;
    setLineup((current) => setSlot(current, location, null));
    if (captainId === playerId) setCaptainId(null);
    if (viceCaptainId === playerId) setViceCaptainId(null);
  };

  const handleMakeCaptain = (playerId: string) => {
    if (viceCaptainId === playerId) setViceCaptainId(captainId);
    setCaptainId(playerId);
  };

  const handleMakeViceCaptain = (playerId: string) => {
    if (captainId === playerId) setCaptainId(viceCaptainId);
    setViceCaptainId(playerId);
  };

  const handleSubmit = async () => {
    if (!isValid || !captainId || !viceCaptainId) return;
    if (!accessToken) {
      setSubmitErrors(['Please sign in again to save your team.']);
      return;
    }
    setSubmitErrors([]);
    setIsSubmitting(true);
    try {
      const players = toSlotOrderedPicks(lineup, captainId, viceCaptainId);
      await upsertPlanner({
        accessToken,
        body: { source: 'MANUAL', players }
      });
      await queryClient.invalidateQueries({ queryKey: ['plannerState'] });
      // AI transfer recommendations are solved against the planner squad, so
      // saving a new team must also throw away suggestions for the old one.
      queryClient.invalidateQueries({
        queryKey: TRANSFER_RECOMMENDATIONS_QUERY_KEY
      });
      onTeamSaved?.();
    } catch (error) {
      if (error instanceof PlannerApiError && error.violations?.length) {
        setSubmitErrors(error.violations.map((violation) => violation.message));
      } else if (error instanceof PlannerApiError && error.details?.length) {
        // The squad upsert names each broken rule in `details` and leaves the
        // envelope message generic, so prefer the rules over "Squad validation
        // failed".
        setSubmitErrors(error.details.map((detail) => detail.message));
      } else if (error instanceof Error) {
        setSubmitErrors([error.message]);
      } else {
        setSubmitErrors(['Something went wrong saving your team.']);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const outstanding = remainingFor(counts);
  const isOverBudget = remainingBank < 0;
  const canSubmit =
    isValid &&
    captainId != null &&
    viceCaptainId != null &&
    !isOverBudget &&
    clubOverflow === null;

  // The save button doubles as the flow's signpost: while the squad is
  // incomplete, over budget or over the club limit it says what still needs
  // doing, so the user never faces a greyed-out button with no explanation.
  const playersRemaining = SQUAD_SIZE - squadIds.length;
  const submitLabel = isSubmitting
    ? 'Saving your team...'
    : isOverBudget
    ? "You're over budget"
    : clubOverflow
    ? 'Too many players from one club'
    : playersRemaining > 0
    ? `Pick ${playersRemaining} more ${
        playersRemaining === 1 ? 'player' : 'players'
      } to save`
    : !captainId || !viceCaptainId
    ? 'Pick a captain and vice-captain to save'
    : 'Save my team';

  return (
    <div className="flex flex-col gap-4 pt-5" data-cy="build-team">
      <div className="flex flex-col items-center gap-2 text-center">
        <H3 className="text-center">Build your team</H3>
        <BodyRegular textColor="text-neutral-600" className="max-w-xl">
          Pick your 15 players to get started. Upload a screenshot of your FPL
          team to fill it in automatically, or add players manually.
        </BodyRegular>
      </div>

      <div className="flex flex-col items-center gap-2">
        <label
          className="inline-flex items-center justify-center font-bold py-2 px-4 rounded-lg bg-primary-600 text-neutral-50 cursor-pointer"
          data-cy="build-team-screenshot"
        >
          {isImporting ? 'Reading screenshot...' : 'Upload FPL team screenshot'}
          <input
            type="file"
            accept="image/*"
            className="hidden"
            disabled={isImporting}
            onChange={handleScreenshotChange}
          />
        </label>
        {importError && (
          <WarningAlert text={importError} data-cy="build-team-import-error" />
        )}
        {onCancel && (
          <Button
            variant="secondary-danger"
            onClick={onCancel}
            dataCy="build-team-cancel"
          >
            Cancel and keep my current team
          </Button>
        )}
      </div>

      <div className="flex items-stretch justify-center gap-8 py-2">
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-neutral-500">
            Players
          </div>
          <div
            className="text-2xl font-bold text-neutral-900 leading-tight"
            data-cy="build-team-player-count"
          >
            {squadIds.length}
            <span className="text-base text-neutral-400">/{SQUAD_SIZE}</span>
          </div>
        </div>
        <div className="w-px bg-neutral-200" aria-hidden />
        <div className="text-center">
          <div className="text-xs uppercase tracking-wide text-neutral-500">
            Money remaining
          </div>
          <div
            className={`text-2xl font-bold leading-tight ${
              isOverBudget ? 'text-danger-600' : 'text-neutral-900'
            }`}
            data-cy="build-team-bank"
          >
            £{remainingBank.toFixed(1)}m
          </div>
        </div>
      </div>

      {outstanding.length > 0 && (
        <BodyRegular
          textColor="text-neutral-600"
          className="text-center"
          data-cy="build-team-outstanding"
        >
          Still to pick: {outstanding.join(', ')}.
        </BodyRegular>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_420px] gap-4">
        <div className="flex flex-col gap-4">
          <BuildLineup
            lineup={lineup}
            captainId={captainId}
            viceCaptainId={viceCaptainId}
            fixturesMap={fixturesMap}
            onEmptySlotPress={handleEmptySlotPress}
            onMakeCaptain={handleMakeCaptain}
            onMakeViceCaptain={handleMakeViceCaptain}
            onRemovePlayer={handleRemovePlayer}
          />

          {clubOverflow && (
            <div data-cy="build-team-club-limit">
              <WarningAlert
                text={`You can only have ${MAX_PLAYERS_PER_CLUB} players from one club. You have ${clubOverflow.count} from ${clubOverflow.teamShortName}.`}
              />
            </div>
          )}

          {submitErrors.length > 0 && (
            <div className="flex flex-col gap-2" data-cy="build-team-errors">
              {submitErrors.map((message) => (
                <WarningAlert key={message} text={message} />
              ))}
            </div>
          )}

          <Button
            variant="primary"
            onClick={handleSubmit}
            disabled={!canSubmit || isSubmitting}
            dataCy="build-team-submit"
          >
            {submitLabel}
          </Button>
        </div>

        <div ref={playerListRef}>
          <PlayerListPanel
            bank={remainingBank}
            onSelectPlayer={handleSelectPlayer}
            requiredPositions={replaceTarget ? [replaceTarget.position] : null}
            disabledPlayerIds={squadIds}
            arePredictionsLocked={isLocked}
          />
        </div>
      </div>
    </div>
  );
};

const firstEmptySlotFor = (
  lineup: ReviewLineup,
  position: BuildPosition
): SlotLocation | null => {
  const index = lineup[position].findIndex((slot) => slot === null);
  return index === -1 ? null : { position, index };
};

export default BuildTeam;
